import type { Profile, ProfileRecommendation, ProfileRecommendationField } from '@/types/api'

import type { ProfileTabKey } from './shared'

export interface MissingItem {
  /** Reads after "Add": "a resume", "your phone", "work history". */
  label: string
  tab: ProfileTabKey
  anchor?: string
}

export interface Completeness {
  percent: number
  missing: MissingItem[]
}

interface Check extends MissingItem {
  weight: number
  done: boolean
}

/**
 * How much of the profile employer forms draw on is filled in, weighted by
 * how often each fact is asked for. Voluntary demographic answers (gender,
 * race, veteran, disability) never count, so declining never lowers it.
 * `resumeCount` is null while resumes are still loading: that check is then
 * left out and the rest scaled up.
 */
export function profileCompleteness(
  profile: Profile,
  resumeCount: number | null,
  recommendations: ProfileRecommendation[] = [],
): Completeness {
  const skills = profile.skills ?? []
  const skillsWithYears = skills.filter((skill) => skill.yearsOfExperience != null).length
  const preference = profile.jobPreference
  const location = profile.location

  const checks: Check[] = [
    {
      label: 'a resume',
      tab: 'profile',
      anchor: 'profile-resume',
      weight: 10,
      done: resumeCount === null ? true : resumeCount > 0,
    },
    { label: 'your name', tab: 'profile', anchor: 'profile-personal', weight: 5, done: Boolean(profile.firstName || profile.fullName) },
    { label: 'your phone', tab: 'profile', anchor: 'profile-personal', weight: 5, done: Boolean(profile.contact?.phoneNumber) },
    {
      label: 'city and country',
      tab: 'profile',
      anchor: 'profile-personal',
      weight: 5,
      done: Boolean(location?.city && (location.countryCode || location.countryName)),
    },
    { label: 'postal code', tab: 'profile', anchor: 'profile-personal', weight: 2, done: Boolean(location?.postalCode) },
    {
      label: 'a headline',
      tab: 'profile',
      anchor: 'profile-career',
      weight: 3,
      done: Boolean(profile.headline || profile.currentTitle),
    },
    {
      label: 'a summary',
      tab: 'profile',
      anchor: 'profile-edit-summary',
      weight: 4,
      done: Boolean(profile.summary?.trim()),
    },
    { label: 'current company', tab: 'profile', anchor: 'profile-career', weight: 1, done: Boolean(profile.currentCompany) },
    { label: 'years of experience', tab: 'profile', anchor: 'profile-career', weight: 5, done: profile.totalExperienceYears != null },
    { label: 'notice period', tab: 'profile', anchor: 'profile-career', weight: 3, done: profile.noticePeriodDays != null },
    { label: 'current salary', tab: 'profile', anchor: 'profile-career', weight: 3, done: profile.currentSalary != null },
    { label: 'expected salary', tab: 'profile', anchor: 'profile-career', weight: 3, done: profile.expectedSalary != null },
    { label: 'LinkedIn', tab: 'profile', anchor: 'profile-links', weight: 4, done: Boolean(profile.contact?.linkedinUrl) },
    {
      label: 'work authorization',
      tab: 'profile',
      anchor: 'profile-work-authorization',
      weight: 8,
      done: Boolean(profile.workEligibility?.workAuthorizations.length),
    },
    { label: 'checklist answers', tab: 'profile', anchor: 'profile-clearance', weight: 5, done: Boolean(profile.checklist) },
    {
      label: skills.length < 5 ? 'five skills' : 'years to your skills',
      tab: 'skills',
      weight: 10,
      done: skills.length >= 5 && skillsWithYears >= 3,
    },
    { label: 'work history', tab: 'work-history', weight: 10, done: (profile.experiences ?? []).length > 0 },
    {
      label: 'education',
      tab: 'profile',
      anchor: 'profile-education',
      weight: 8,
      done: Boolean(
        profile.educationUniversity && profile.educationCourse && profile.educationGraduationYear != null,
      ),
    },
    {
      label: 'job preferences',
      tab: 'preferences',
      weight: 6,
      done: Boolean(preference?.desiredTitles.length || preference?.employmentTypes.length),
    },
  ]

  const counted = resumeCount === null ? checks.filter((check) => check.label !== 'a resume') : checks
  const total = counted.reduce((sum, check) => sum + check.weight, 0)
  const earned = counted.filter((check) => check.done).reduce((sum, check) => sum + check.weight, 0)
  const percent = total === 0 ? 0 : Math.round((earned / total) * 100)

  // What applications actually asked for leads the list.
  const asked = recommendations
    .map((rec) => RECOMMENDATION_ITEM[rec.field]?.(rec))
    .filter((item): item is MissingItem => Boolean(item))
  const missing: MissingItem[] = []
  for (const item of [...asked, ...counted.filter((check) => !check.done)]) {
    if (!missing.some((existing) => existing.label === item.label)) {
      missing.push({ label: item.label, tab: item.tab, anchor: item.anchor })
    }
  }

  return { percent, missing }
}

/**
 * The quick "Add X" chips: what is still missing, minus what the
 * recommendations above already ask for, so nothing is listed twice.
 */
export function missingChips(missing: MissingItem[], recommendations: ProfileRecommendation[]): MissingItem[] {
  const covered = new Set(
    recommendations
      .map((rec) => RECOMMENDATION_ITEM[rec.field]?.(rec)?.label)
      .filter((label): label is string => Boolean(label)),
  )
  return missing.filter((item) => !covered.has(item.label))
}

const RECOMMENDATION_ITEM: Partial<
  Record<ProfileRecommendationField, (rec: ProfileRecommendation) => MissingItem | null>
> = {
  current_salary: () => ({ label: 'current salary', tab: 'profile', anchor: 'profile-edit-currentSalary' }),
  ctc: () => ({ label: 'current salary', tab: 'profile', anchor: 'profile-edit-currentSalary' }),
  expected_salary: () => ({ label: 'expected salary', tab: 'profile', anchor: 'profile-edit-expectedSalary' }),
  notice_period: () => ({ label: 'notice period', tab: 'profile', anchor: 'profile-edit-noticePeriodDays' }),
  skill_years: (rec) => (rec.skill ? { label: `years of ${rec.skill}`, tab: 'skills' } : null),
  current_title: () => ({ label: 'current title', tab: 'profile', anchor: 'profile-edit-currentTitle' }),
  current_company: () => ({ label: 'current company', tab: 'profile', anchor: 'profile-edit-currentCompany' }),
  linkedin: () => ({ label: 'LinkedIn', tab: 'profile', anchor: 'profile-edit-linkedinUrl' }),
  skills: () => ({ label: 'skills', tab: 'skills' }),
  additional_notes: () => ({ label: 'cloud experience', tab: 'profile', anchor: 'profile-edit-note-cloud' }),
}
